import { useRef, useState } from 'react';
import cx from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y, Keyboard } from 'swiper/modules';
import { type Swiper as SwiperType } from 'swiper/types';
import config from '@plone/registry';

import { useIntl, defineMessages } from 'react-intl';

import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/navigation';

import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';

import type { ProductsSliderData } from '@redturtle/qubicaamf-theme/components/Blocks/ProductsSlider/schema';
import VideoSlide from '@redturtle/qubicaamf-theme/components/Blocks/ProductsSlider/Slider/VideoSlide';
import ImageSlide from '@redturtle/qubicaamf-theme/components/Blocks/ProductsSlider/Slider/ImageSlide';
import styles from '@redturtle/qubicaamf-theme/components/Blocks/ProductsSlider/styles.module.css';

interface SliderProps extends Pick<ProductsSliderData, 'slides'> {
  isEditMode?: boolean;
}

const Slider = (props: SliderProps) => {
  const intl = useIntl();
  const [swiperInstance, setSwiperInstance] = useState<SwiperType>();
  const Image = config.getComponent('Image').component;

  const { slides = [] } = props;

  const swiper = useRef();

  const swiperConfig = {
    modules: [Navigation, A11y, Keyboard],
    navigation: true,
    className: 'products-slider',
    loop: true,
    centeredSlides: true,
    spaceBetween: 10,
    lazy: 'true',
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    a11y: {
      enabled: true,
      containerMessage: intl.formatMessage(messages.slider),
      containerRole: 'region',
      firstSlideMessage: intl.formatMessage(messages.firstSlideMessage),
      lastSlideMessage: intl.formatMessage(messages.lastSlideMessage),
      nextSlideMessage: intl.formatMessage(messages.nextSlideMessage),
      paginationBulletMessage: intl.formatMessage(
        messages.paginationBulletMessage,
      ),
      prevSlideMessage: intl.formatMessage(messages.prevSlideMessage),
      slideLabelMessage: 'Slide {{index}} / {{slidesLength}}',
    },
    breakpoints: {
      320: {
        slidesPerView: 1.2,
      },
      // when window width is >= 640px
      640: {
        slidesPerView: 3.2,
      },
      1024: {
        slidesPerView: 3.2,
      },
    },
  };

  // bug swiper: slidesPerView is always rounded up, and loop only works with
  // a number of slides equal or greater than slidesPerView + slidesPerGroup (4+1 = 5)
  // known bug: it is suggested to just duplicate the number of slides

  const swiperSlides =
    slides?.length > 5 ? slides : [...slides, ...slides.map((el) => el)];

  // bug swiper: https://github.com/nolimits4web/swiper/issues/7790
  // keyboard paginate all swiper elements on the page

  return (
    <>
      <Swiper
        {...swiperConfig}
        ref={swiper}
        onSwiper={(swiper) => {
          if (!swiperInstance) {
            setSwiperInstance(swiper);
          }
        }}
        watchSlidesProgress={true}
      >
        {swiperSlides.map((slide, index) => {
          const { link, slide_content, title, title_color, show_overlay } =
            slide;

          return (
            <SwiperSlide
              key={slide['@id'] + index}
              aria-roledescription="slide"
            >
              {({ isActive }) => (
                <div className={styles.slide}>
                  {slide_content?.['@type'] === 'WildcardVideo' && (
                    <VideoSlide
                      slide_content={slide_content}
                      isActive={isActive}
                    />
                  )}
                  {slide_content?.['@type'] === 'Image' && (
                    <ImageSlide slide_content={slide_content} />
                  )}
                  <div className={cx({ [styles.showOverlay]: show_overlay })} />
                  <div
                    className={cx(styles.slideTitleContainer, {
                      [styles.blackTitle]: title_color === 'black',
                      [styles.whiteTitle]: title_color === 'white',
                    })}
                  >
                    {link ? (
                      <UniversalLink
                        item={link}
                        href={link['@id']}
                        openLinkInNewTab={false}
                      >
                        {title}
                      </UniversalLink>
                    ) : (
                      title
                    )}
                  </div>
                </div>
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </>
  );
};

export default Slider;

const messages = defineMessages({
  slider: {
    id: 'slider',
    defaultMessage: 'Slider',
  },
  firstSlideMessage: {
    id: 'This is the first slide',
    defaultMessage: 'This is the first slide of the slider',
  },
  lastSlideMessage: {
    id: 'This is the last slide',
    defaultMessage: 'This is the first slide of the slider',
  },
  nextSlideMessage: {
    id: 'Next slide',
    defaultMessage: 'Next slide',
  },
  paginationBulletMessage: {
    id: 'Go to slide {{index}}',
    defaultMessage: 'Vai alla slide {index}',
  },
  prevSlideMessage: {
    id: 'Previous slide',
    defaultMessage: 'Slide precedente',
  },
  play: {
    id: 'Carousel play',
    defaultMessage: 'Seleziona per riprodurre',
  },
  pause: {
    id: 'Carousel pause',
    defaultMessage: 'Metti in pausa',
  },
});
